import { useEffect, useRef } from 'react';
import { Image, withContent } from '@bubbletea/core';
import { useTheme } from 'styled-components';
import uniqueId from 'lodash/uniqueId';
import { OffersItem } from '../../../../types/generatedContent';
import { IMainTheme } from '../../../../types/theme';

import { UbiplusIcon } from '../../../UI/Icons';
import Title from '../../../UI/Title';

import { Details, PromoFeaturesWrapper, PromoFeature, PromoCardWrapper, UbiPlusSection, PromoDetails } from './styles';
import sendEvent from '../../../../utils/sendEvent';

interface IFeatureProps {
  offer: OffersItem;
  hasAltTitleColor?: boolean;
  index: number;
}

const PromoCard = ({ offer, hasAltTitleColor, index }: IFeatureProps) => {
  const promoCardContent = useRef<HTMLParagraphElement>(null);
  const theme = useTheme() as IMainTheme;
  const iconFormat = (iconName: string) => {
    return theme.name === 'uplay' ? iconName.replace('.svg', '-upc.svg') : iconName;
  };

  const sendUbisoftPlusTracking = () => {
    const eventDetails = {
      location: 'promotional offers',
      category: 'action',
      locationPosition: 5,
      action: 'ubisoft plus : see more',
    };
    sendEvent(eventDetails);
  };

  useEffect(() => {
    const seeTermsLink = promoCardContent.current?.querySelector('a');
    if (seeTermsLink) {
      const promoType = seeTermsLink.href.indexOf('faq10') > -1 ? 'ubisoft plus' : 'rocksmith 2014 bonus';
      const eventDetails = {
        location: 'promotional offers',
        category: 'interaction',
        locationPosition: 5,
        action: `${promoType} : see terms`,
      };
      seeTermsLink.removeEventListener('click', () => {
        sendEvent(eventDetails);
      });
      seeTermsLink.addEventListener('click', () => {
        sendEvent(eventDetails);
      });
    }
  }, [promoCardContent]);

  return (
    <PromoCardWrapper
      initial={{
        opacity: 0,
        y: 3,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
      }}
      transition={{
        type: 'spring',
        delay: 0.2 + index * 0.1,
      }}
      viewport={{ once: true }}
    >
      <Image imageNames={[offer.image as string]} altText={offer.imageAlt as string} dynamic />
      <Details>
        <Title hasAltColor={hasAltTitleColor} as="h3">
          {offer.title}
        </Title>
        <PromoDetails dangerouslySetInnerHTML={{ __html: offer.description as string }} ref={promoCardContent} />
        <PromoFeaturesWrapper>
          {offer.features.map((feature) => (
            <PromoFeature key={`promo_feature_${uniqueId()}`}>
              {feature.icon && (
                <Image imageNames={[iconFormat(feature.icon as string)]} altText={offer.imageAlt as string} dynamic />
              )}
              <p dangerouslySetInnerHTML={{ __html: feature.description as string }} />
            </PromoFeature>
          ))}
        </PromoFeaturesWrapper>
      </Details>
      {offer.more && (
        <UbiPlusSection>
          <UbiplusIcon />
          <a href="/ubisoftplus" target="_blank" onClick={() => sendUbisoftPlusTracking()}>
            {offer.more.description}
          </a>
        </UbiPlusSection>
      )}
    </PromoCardWrapper>
  );
};

export default withContent(PromoCard);
